import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

const AppointmentPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '', 
    phone: '',
    email: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    notes: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Book DMV Appointment Las Vegas Nevada | Speedy DMV Services',
    description: 'Schedule your Nevada DMV service appointment online or call 702.979.7202. Book vehicle registration, title transfer, license plates, notary, and more at Speedy DMV Services, 4525 S Sandhill Rd, Las Vegas.',
    keywords: 'DMV appointment Las Vegas, book DMV service Nevada, schedule vehicle registration Las Vegas, DMV appointment Las Vegas NV, Speedy DMV appointment',
    canonical: `${siteUrl}/appointment`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateWebPageSchema(
          'Book a DMV Service Appointment — Speedy DMV Services Las Vegas',
          'Schedule an appointment for fast Nevada DMV services in Las Vegas. Vehicle registration, title transfer, license plates, temporary permits, and notary services available.',
          `${siteUrl}/appointment`
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Book Appointment', url: '/appointment' }
        ])
      ]
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      
      const response = await fetch('https://readdy.ai/api/form/d4to47qtj9hepa8fj6og', {
        method: 'POST',
        body: formDataToSend
      });
      
      if (response.ok) {
        setSubmitMessage('Appointment request submitted successfully! We\'ll call you within 2 hours to confirm.');
        setFormData({
          firstName: '',
          lastName: '', 
          phone: '',
          email: '',
          service: '',
          preferredDate: '',
          preferredTime: '',
          vehicleYear: '',
          vehicleMake: '',
          vehicleModel: '',
          notes: ''
        });
      } else {
        setSubmitMessage('There was an error submitting your request. Please call us directly at 702.979.7202');
      }
    } catch (error) {
      setSubmitMessage('There was an error submitting your request. Please call us directly at 702.979.7202');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ed13f0cc5aa1a4b01057f69fecde8e1b.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Schedule Your DMV Service Appointment
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Book your Nevada DMV services appointment online or call 702.979.7202. We'll handle your vehicle registration, title transfer, or other DMV needs quickly and professionally.
            </p>
          </div>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-phone text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Call Direct</p>
                      <a href="tel:702.979.7202" className="text-teal-600 hover:text-teal-700 font-medium">
                        702.979.7202
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-map-marker-alt text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Our Location</p>
                      <p className="text-gray-600 text-sm">4525 S Sandhill Rd<br />Las Vegas, NV</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-clock text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Business Hours</p>
                      <div className="text-gray-600 text-sm space-y-1">
                        <div>Mon-Fri: 9:00 AM - 5:00 PM</div>
                        <div>Saturday: 10:00 AM - 3:00 PM</div>
                        <div>Sunday: Closed</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <i className="fas fa-exclamation-circle text-orange-600"></i>
                    <p className="font-semibold text-orange-800 text-sm">Appointments Required</p>
                  </div>
                  <p className="text-xs text-orange-700">
                    Please call ahead or submit this form. We'll confirm your appointment within 2 hours.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Appointment Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-8">Request Appointment</h2>
                
                {submitMessage && (
                  <div className={`mb-6 p-4 rounded-lg ${
                    submitMessage.includes('successfully') 
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    <p>{submitMessage}</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} data-readdy-form>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input 
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="(702) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Needed *</label>
                    <select 
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select a service...</option>
                      <option value="vehicle-registration">Vehicle Registration</option>
                      <option value="title-transfer">Title Transfer</option>
                      <option value="license-plates">License Plates</option>
                      <option value="duplicate-title">Duplicate Title</option>
                      <option value="temporary-permits">Temporary Permits</option>
                      <option value="notary-services">Notary Services</option>
                      <option value="registration-renewal">Registration Renewal</option>
                      <option value="out-of-state-transfer">Out-of-State Transfer</option>
                      <option value="multiple-services">Multiple Services</option>
                      <option value="other">Other - Please Specify in Notes</option>
                    </select>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date</label>
                      <input 
                        type="date" 
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time</label>
                      <select 
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="">Select preferred time...</option>
                        <option value="9:00 AM">9:00 AM</option>
                        <option value="9:30 AM">9:30 AM</option>
                        <option value="10:00 AM">10:00 AM</option>
                        <option value="10:30 AM">10:30 AM</option>
                        <option value="11:00 AM">11:00 AM</option>
                        <option value="11:30 AM">11:30 AM</option>
                        <option value="12:00 PM">12:00 PM</option>
                        <option value="12:30 PM">12:30 PM</option>
                        <option value="1:00 PM">1:00 PM</option>
                        <option value="1:30 PM">1:30 PM</option>
                        <option value="2:00 PM">2:00 PM</option>
                        <option value="2:30 PM">2:30 PM</option>
                        <option value="3:00 PM">3:00 PM</option>
                        <option value="3:30 PM">3:30 PM</option>
                        <option value="4:00 PM">4:00 PM</option>
                        <option value="4:30 PM">4:30 PM</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Year</label>
                      <input 
                        type="text" 
                        name="vehicleYear"
                        value={formData.vehicleYear}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="e.g. 2020"
                        maxLength={4}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Make</label>
                      <input 
                        type="text" 
                        name="vehicleMake"
                        value={formData.vehicleMake}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="e.g. Honda"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Model</label>
                      <input 
                        type="text" 
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="e.g. Civic"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                    <textarea 
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                      placeholder="Any additional information or special requests..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 characters</p>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`w-full px-8 py-4 rounded-lg font-semibold text-white transition-colors whitespace-nowrap ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Appointment'}
                  </button>
                  
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    We'll call you within 2 hours to confirm your appointment details.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Services Available by Appointment
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Vehicle Registration",
                icon: "fas fa-car",
                time: "30-45 minutes",
                description: "New registrations, renewals, out-of-state transfers"
              },
              {
                title: "Title Transfer",
                icon: "fas fa-exchange-alt",
                time: "20-30 minutes",
                description: "Private party sales, dealership purchases, gifts"
              },
              {
                title: "License Plates",
                icon: "fas fa-square", 
                time: "15-20 minutes",
                description: "Standard, specialty, and personalized plates"
              },
              {
                title: "Duplicate Title",
                icon: "fas fa-copy",
                time: "20-25 minutes", 
                description: "Lost, stolen, or damaged title replacement"
              },
              {
                title: "Temporary Permits",
                icon: "fas fa-calendar-alt",
                time: "10-15 minutes",
                description: "30-day and 60-day movement permits"
              },
              {
                title: "Notary Services", 
                icon: "fas fa-stamp",
                time: "5-10 minutes",
                description: "Document and title notarization"
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-2xl text-teal-600`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm text-gray-500 mb-3">Typical time: {service.time}</p>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Book Appointment */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Book an Appointment?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Scheduling ahead ensures fast service and guarantees we have time for your specific needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "fas fa-clock", 
                title: "No Waiting",
                description: "Skip the lines with your scheduled appointment time"
              },
              {
                icon: "fas fa-file-check",
                title: "Document Review",
                description: "We'll confirm you have all required documents ahead of time"
              },
              {
                icon: "fas fa-user-tie",
                title: "Dedicated Service",
                description: "Our team will be prepared for your specific service needs"
              },
              {
                icon: "fas fa-calendar-check",
                title: "Guaranteed Time",
                description: "Your time slot is reserved - no rushing or delays"
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AppointmentPage;
